import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import { ObfuscatedEmail } from '../components/ObfuscatedEmail';

export default function ConfidentialitePage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <SeoMeta
        title="Politique de Confidentialité"
        description="Politique RGPD Drone Maps : données collectées, finalités, durée de conservation, vos droits (accès, rectification, effacement, portabilité)."
        keywords="RGPD Drone Maps, confidentialité drone, données personnelles drone, protection données drone SaaS"
      />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 mb-8">
          <ArrowLeft className="w-4 h-4" />
          Retour à l'accueil
        </Link>
        
        <h1 className="text-3xl font-bold text-slate-900 mb-8 font-[Barlow]">Politique de Confidentialité</h1>
        
        <div className="bg-white rounded-xl p-8 shadow-sm space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Responsable du traitement</h2>
            <p className="text-slate-600 leading-relaxed">
              Le responsable du traitement des données personnelles est :<br /><br />
              <strong>BOD CORP</strong><br />
              60 RUE FRANCOIS IER, 75008 PARIS<br />
              SIRET : 48332066900037<br />
              Email : <ObfuscatedEmail linkClassName="text-sky-600 hover:underline" />
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Données collectées</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Dans le cadre de l'utilisation de Drone Maps, nous collectons les données suivantes :
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li><strong>Données d'identification</strong> : nom, prénom, adresse email</li>
              <li><strong>Données de connexion</strong> : adresse IP, logs de connexion, type de navigateur</li>
              <li><strong>Données d'utilisation</strong> : missions créées, zones de vol, paramètres de drones</li>
              <li><strong>Données de paiement</strong> : traitées de manière sécurisée par Stripe (nous ne stockons pas vos coordonnées bancaires)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Finalités du traitement</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Vos données sont collectées pour les finalités suivantes :
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Création et gestion de votre compte utilisateur</li>
              <li>Fourniture du service d'analyse réglementaire pour vols de drones</li>
              <li>Génération de rapports de mission PDF</li>
              <li>Gestion des abonnements et facturation</li>
              <li>Amélioration de nos services et support client</li>
              <li>Respect de nos obligations légales</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Base légale du traitement</h2>
            <p className="text-slate-600 leading-relaxed">
              Le traitement de vos données repose sur :<br /><br />
              • <strong>L'exécution du contrat</strong> : pour la fourniture de nos services<br />
              • <strong>Votre consentement</strong> : pour les cookies non essentiels et les communications marketing<br />
              • <strong>L'intérêt légitime</strong> : pour l'amélioration de nos services et la sécurité<br />
              • <strong>L'obligation légale</strong> : pour la conservation des données de facturation
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Durée de conservation</h2>
            <p className="text-slate-600 leading-relaxed">
              Vos données sont conservées pendant les durées suivantes :<br /><br />
              • <strong>Données de compte</strong> : pendant toute la durée de votre inscription + 3 ans après suppression<br />
              • <strong>Données de mission</strong> : 5 ans à compter de la création<br />
              • <strong>Données de facturation</strong> : 10 ans (obligation légale)<br />
              • <strong>Cookies</strong> : 13 mois maximum
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Destinataires des données</h2>
            <p className="text-slate-600 leading-relaxed">
              Vos données peuvent être transmises à :<br /><br />
              • <strong>Stripe</strong> : pour le traitement des paiements<br />
              • <strong>Emergent</strong> : hébergeur de l'application<br />
              • <strong>Autorités compétentes</strong> : sur demande légale<br /><br />
              Nous ne vendons jamais vos données à des tiers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Transferts hors UE</h2>
            <p className="text-slate-600 leading-relaxed">
              Certaines données peuvent être transférées vers des pays hors de l'Union Européenne (notamment pour 
              les services d'hébergement et de paiement). Ces transferts sont encadrés par des garanties appropriées 
              (clauses contractuelles types, certification Privacy Shield, etc.).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Vos droits</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Conformément au RGPD, vous disposez des droits suivants :
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li><strong>Droit d'accès</strong> : obtenir une copie de vos données</li>
              <li><strong>Droit de rectification</strong> : corriger vos données inexactes</li>
              <li><strong>Droit à l'effacement</strong> : demander la suppression de vos données</li>
              <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré</li>
              <li><strong>Droit d'opposition</strong> : vous opposer au traitement de vos données</li>
              <li><strong>Droit à la limitation</strong> : limiter le traitement de vos données</li>
              <li><strong>Droit de retirer votre consentement</strong> : à tout moment pour les traitements basés sur le consentement</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              Pour exercer ces droits, contactez-nous à : <ObfuscatedEmail linkClassName="text-sky-600 hover:underline" />
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Sécurité</h2>
            <p className="text-slate-600 leading-relaxed">
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données 
              contre tout accès non autorisé, perte, altération ou divulgation :<br /><br />
              • Chiffrement des données en transit (HTTPS/TLS)<br />
              • Chiffrement des mots de passe (bcrypt)<br />
              • Authentification sécurisée (JWT)<br />
              • Accès restreint aux données personnelles
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Réclamation</h2>
            <p className="text-slate-600 leading-relaxed">
              Si vous estimez que le traitement de vos données n'est pas conforme à la réglementation, vous pouvez 
              introduire une réclamation auprès de la CNIL :<br /><br />
              <strong>Commission Nationale de l'Informatique et des Libertés</strong><br />
              3 Place de Fontenoy, TSA 80715<br />
              75334 PARIS CEDEX 07<br />
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">www.cnil.fr</a>
            </p>
          </section>

          <p className="text-sm text-slate-400 pt-4 border-t border-slate-200">
            Dernière mise à jour : Février 2026
          </p>
        </div>
      </div>
    </div>
  );
}
